<template>
  <div class="electrical-manager">
    <!-- Add MSB Dialog -->
    <AddMsbDialog
      v-model="showMSBDialog"
      :form-data="msbForm"
      :location-options="locationOptions"
      :saving="savingMSB"
      @update:name="msbForm.name = $event"
      @update:description="msbForm.description = $event"
      @update:voltage-rating="msbForm.voltage_rating = $event"
      @update:current-rating="msbForm.current_rating = $event"
      @update:manufacturer="msbForm.manufacturer = $event"
      @update:model="msbForm.model = $event"
      @update:location="msbForm.location = $event"
      @submit="saveMSB"
      @reset="resetMSBForm"
    />

    <!-- Add MSSB Dialog -->
    <AddMssbDialog
      v-model="showMSSBDialog"
      :form-data="mssbForm"
      :msb-options="msbOptions"
      :service-type-options="serviceTypeOptions"
      :location-options="locationOptions"
      :saving="savingMSSB"
      @update:parent-msb="mssbForm.parent_msb = $event"
      @update:name="mssbForm.name = $event"
      @update:description="mssbForm.description = $event"
      @update:current-rating="mssbForm.current_rating = $event"
      @update:service-type="mssbForm.service_type = $event"
      @update:location="mssbForm.location = $event"
      @submit="saveMSSB"
      @reset="resetMSSBForm"
    />

    <!-- Add DDC Dialog -->
    <AddDdcDialog
      v-model="showDDCDialog"
      :form-data="ddcForm"
      :mssb-options="mssbOptions"
      :controller-type-options="controllerTypeOptions"
      :location-options="locationOptions"
      :saving="savingDDC"
      @update:parent-mssb="ddcForm.parent_mssb = $event"
      @update:name="ddcForm.name = $event"
      @update:description="ddcForm.description = $event"
      @update:number-of-points="ddcForm.number_of_points = $event"
      @update:controller-type="ddcForm.controller_type = $event"
      @update:location="ddcForm.location = $event"
      @submit="saveDDC"
      @reset="resetDDCForm"
    />

    <!-- DDC Allocation Dialog -->
    <DdcAllocationDialog
      v-model="showDdcAllocationDialog"
      :form-data="ddcAllocationForm"
      :available-ddc-panels="availableDdcPanels"
      :saving="savingDdcAllocation"
      @update:ddc-panel-id="ddcAllocationForm.ddc_panel_id = $event"
      @update:apply-to-devices="ddcAllocationForm.apply_to_devices = $event"
      @submit="saveDdcAllocation"
    />

    <!-- Power Connection Dialog -->
    <PowerConnectionDialog
      v-model="showPowerConnectionDialog"
      :form-data="powerConnectionForm"
      :available-power-sources="availablePowerSources"
      :saving="savingPowerConnection"
      @update:power-source-id="powerConnectionForm.power_source_id = $event"
      @submit="savePowerConnection"
    />

    <!-- Switchboard Classification Dialog -->
    <SwitchboardClassificationDialog
      v-model="showSwitchboardClassificationDialog"
      :form-data="switchboardClassificationForm"
      :saving="savingSwitchboardClassification"
      @update:classification="switchboardClassificationForm.classification = $event"
      @submit="saveSwitchboardClassification"
    />

    <!-- Add Switchboard Dialog -->
    <AddSwitchboardDialog
      v-model="showAddSwitchboardDialog"
      :form-data="newSwitchboard"
      :switchboard-types="switchboardTypes"
      @update:name="newSwitchboard.name = $event"
      @update:type="newSwitchboard.type = $event"
      @update:description="newSwitchboard.description = $event"
      @submit="confirmAddSwitchboard"
    />

    <!-- Add Existing Switchboard Dialog -->
    <AddExistingSwitchboardDialog
      v-model="showAddExistingSwitchboardDialog"
      :selected-switchboard="selectedExistingSwitchboard"
      :available-switchboards="availableSwitchboardsForParent"
      @update:selected="selectedExistingSwitchboard = $event"
      @submit="addExistingSwitchboard"
    />

    <!-- Unallocated Switchboards Dialog -->
    <UnallocatedSwitchboardsDialog
      v-model="showUnallocatedDialog"
      :unallocated-type="unallocatedType"
      :badge-color="getUnallocatedBadgeColor()"
      :item-count="getUnallocatedCount()"
      :filter-text="unallocatedFilter"
      :filtered-list="filteredUnallocatedList"
      @update:filter="unallocatedFilter = $event"
      @quick-add="quickAddToTree($event)"
    />
  </div>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import { useBrickClassOptions } from '@/composables/brick-schema';
  import { useElectricalApi } from '@/composables/brick-schema/api';

  // Import dialog components
  import AddMsbDialog from '../dialogs/electrical/AddMsbDialog.vue';
  import AddMssbDialog from '../dialogs/electrical/AddMssbDialog.vue';
  import AddDdcDialog from '../dialogs/electrical/AddDdcDialog.vue';
  import DdcAllocationDialog from '../dialogs/electrical/DdcAllocationDialog.vue';
  import PowerConnectionDialog from '../dialogs/electrical/PowerConnectionDialog.vue';
  import SwitchboardClassificationDialog from '../dialogs/electrical/SwitchboardClassificationDialog.vue';
  import AddSwitchboardDialog from '../dialogs/AddSwitchboardDialog.vue';
  import AddExistingSwitchboardDialog from '../dialogs/AddExistingSwitchboardDialog.vue';
  import UnallocatedSwitchboardsDialog from '../dialogs/UnallocatedSwitchboardsDialog.vue';

  // Props
  const props = defineProps({
    siteId: {
      type: [String, Number],
      required: true,
    },
    localHierarchyData: {
      type: Object,
      default: () => ({}),
    },
    equipmentList: {
      type: Array,
      default: () => [],
    },
    electricalData: {
      type: Array,
      default: () => [],
    },
    selectedElectricalNode: {
      type: Object,
      default: null,
    },
  });

  // Emits
  const emit = defineEmits([
    'refresh-data',
    'refresh-electrical-hierarchy',
    'msb-created',
    'mssb-created',
    'ddc-created',
    'ddc-allocated',
    'ddc-deallocated',
    'power-connected',
    'power-disconnected',
    'switchboard-classified',
    'switchboard-added',
  ]);

  // Inject shared context from parent
  const schemaContext = inject('schemaContext', null);

  // Quasar
  const $q = useQuasar();

  // Composables
  const {
    serviceTypeOptions,
    controllerTypeOptions,
  } = useBrickClassOptions();

  const siteIdRef = computed(() => props.siteId);
  const electricalApi = useElectricalApi(siteIdRef);

  // ===================================
  // DIALOG VISIBILITY STATES
  // ===================================
  const showMSBDialog = ref(false);
  const showMSSBDialog = ref(false);
  const showDDCDialog = ref(false);
  const showDdcAllocationDialog = ref(false);
  const showPowerConnectionDialog = ref(false);
  const showSwitchboardClassificationDialog = ref(false);
  const showAddSwitchboardDialog = ref(false);
  const showAddExistingSwitchboardDialog = ref(false);
  const showUnallocatedDialog = ref(false);

  // ===================================
  // SAVING STATES
  // ===================================
  const savingMSB = ref(false);
  const savingMSSB = ref(false);
  const savingDDC = ref(false);
  const savingDdcAllocation = ref(false);
  const savingPowerConnection = ref(false);
  const savingSwitchboardClassification = ref(false);

  // ===================================
  // FORM DATA
  // ===================================
  const msbForm = ref({
    name: '',
    description: '',
    voltage_rating: '415V',
    current_rating: '',
    manufacturer: '',
    model: '',
    location: null,
  });

  const mssbForm = ref({
    parent_msb: null,
    name: '',
    description: '',
    current_rating: '',
    service_type: null,
    location: null,
  });

  const ddcForm = ref({
    parent_mssb: null,
    name: '',
    description: '',
    number_of_points: 0,
    controller_type: null,
    location: null,
  });

  const ddcAllocationForm = ref({
    equipment_id: null,
    equipment_name: '',
    ddc_panel_id: null,
    apply_to_devices: true,
  });

  const powerConnectionForm = ref({
    equipment_id: null,
    equipment_name: '',
    power_source_id: null,
    power_source_type: '',
  });

  const switchboardClassificationForm = ref({
    equipment_id: null,
    classification: null,
  });

  const newSwitchboard = ref({
    name: '',
    type: '',
    space_id: null,
    brick_class: '',
    properties: {},
  });

  // ===================================
  // SELECTION STATE
  // ===================================
  const parentElectricalNode = ref(null);
  const selectedExistingSwitchboard = ref(null);
  const unallocatedType = ref('');
  const unallocatedFilter = ref('');

  // ===================================
  // DATA REFS
  // ===================================
  const availableDdcPanels = ref([]);
  const availablePowerSources = ref([]);
  const switchboardTypes = [
    { label: 'Main Switchboard (MSB)', value: 'MSB', brick_class: 'brick:Main_Switchboard' },
    { label: 'Mechanical Services Switchboard (MSSB)', value: 'MSSB', brick_class: 'brick:Mechanical_Services_Switchboard' },
    { label: 'Distribution Board (DB)', value: 'DB', brick_class: 'brick:Distribution_Board' },
    { label: 'DDC Panel', value: 'DDC', brick_class: 'brick:DDC_Panel' },
  ];

  // ===================================
  // COMPUTED PROPERTIES
  // ===================================
  const locationOptions = computed(() => {
    const options = [];

    if (props.localHierarchyData && props.localHierarchyData.buildings) {
      props.localHierarchyData.buildings.forEach((building) => {
        if (building.levels) {
          building.levels.forEach((level) => {
            if (level.spaces) {
              level.spaces.forEach((space) => {
                options.push({
                  label: `${building.name} > ${level.name} > ${space.name}`,
                  value: space.id,
                });
              });
            }
          });
        }
      });
    }

    return options;
  });

  const msbOptions = computed(() => {
    return props.equipmentList
      .filter((eq) => eq.brick_class === 'brick:Main_Switchboard')
      .map((eq) => ({
        label: eq.name,
        value: eq.id,
      }));
  });

  const mssbOptions = computed(() => {
    return props.equipmentList
      .filter((eq) => eq.brick_class === 'brick:Mechanical_Services_Switchboard')
      .map((eq) => ({
        label: eq.name,
        value: eq.id,
      }));
  });

  const unallocatedSwitchboards = computed(() => {
    const allocated = new Set();

    const collectAllocated = (nodes) => {
      if (!nodes) {return;}
      nodes.forEach((node) => {
        if (node.id) {allocated.add(node.id);}
        if (node.children) {collectAllocated(node.children);}
      });
    };

    if (props.electricalData) {
      collectAllocated(props.electricalData);
    }

    return {
      msb: props.equipmentList.filter((eq) =>
        eq.brick_class === 'brick:Main_Switchboard' && !allocated.has(eq.id),
      ),
      mssb: props.equipmentList.filter((eq) =>
        eq.brick_class === 'brick:Mechanical_Services_Switchboard' && !allocated.has(eq.id),
      ),
      db: props.equipmentList.filter((eq) =>
        eq.brick_class === 'brick:Distribution_Board' && !allocated.has(eq.id),
      ),
      ddc: props.equipmentList.filter((eq) =>
        eq.brick_class === 'brick:DDC_Panel' && !allocated.has(eq.id),
      ),
    };
  });

  const filteredUnallocatedList = computed(() => {
    let list = [];

    switch (unallocatedType.value) {
      case 'MSB':
        list = unallocatedSwitchboards.value.msb;
        break;
      case 'MSSB':
        list = unallocatedSwitchboards.value.mssb;
        break;
      case 'DB':
        list = unallocatedSwitchboards.value.db;
        break;
      case 'DDC':
        list = unallocatedSwitchboards.value.ddc;
        break;
    }

    if (unallocatedFilter.value) {
      const filter = unallocatedFilter.value.toLowerCase();
      list = list.filter((sb) =>
        sb.name.toLowerCase().includes(filter),
      );
    }

    return list.map((sb) => ({
      ...sb,
      icon: getSwitchboardIcon(sb),
      color: getSwitchboardColor(sb),
      location: getEquipmentLocation(sb),
      canAdd: canQuickAdd(sb),
    }));
  });

  const availableSwitchboardsForParent = computed(() => {
    if (!parentElectricalNode.value) {return [];}

    const parentClass = parentElectricalNode.value.brick_class || '';

    if (parentClass.includes('Transformer')) {
      return unallocatedSwitchboards.value.msb;
    } else if (parentClass.includes('Main_Switchboard')) {
      return [
        ...unallocatedSwitchboards.value.mssb,
        ...unallocatedSwitchboards.value.db,
      ];
    } else if (parentClass.includes('Mechanical_Services_Switchboard')) {
      return [
        ...unallocatedSwitchboards.value.ddc,
        ...unallocatedSwitchboards.value.db,
      ];
    }

    return [];
  });

  // ===================================
  // MSB OPERATIONS
  // ===================================

  function showAddMSBDialog() {
    resetMSBForm();
    showMSBDialog.value = true;
  }

  function resetMSBForm() {
    msbForm.value = {
      name: '',
      description: '',
      voltage_rating: '415V',
      current_rating: '',
      manufacturer: '',
      model: '',
      location: null,
    };
  }

  async function saveMSB() {
    savingMSB.value = true;
    try {
      await electricalApi.createMSB({
        name: msbForm.value.name,
        description: msbForm.value.description,
        voltage_rating: msbForm.value.voltage_rating,
        current_rating: msbForm.value.current_rating,
        manufacturer: msbForm.value.manufacturer,
        model: msbForm.value.model,
        location: msbForm.value.location,
      });

      showMSBDialog.value = false;
      resetMSBForm();

      $q.notify({
        type: 'positive',
        message: 'MSB created successfully',
      });

      emit('msb-created');
      emit('refresh-electrical-hierarchy');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create MSB: ${error.message}`,
      });
    } finally {
      savingMSB.value = false;
    }
  }

  // ===================================
  // MSSB OPERATIONS
  // ===================================

  function showAddMSSBDialog() {
    if (msbOptions.value.length === 0) {
      $q.notify({
        type: 'warning',
        message: 'Please add at least one MSB first',
      });
      return;
    }

    resetMSSBForm();
    showMSSBDialog.value = true;
  }

  function resetMSSBForm() {
    mssbForm.value = {
      parent_msb: null,
      name: '',
      description: '',
      current_rating: '',
      service_type: null,
      location: null,
    };
  }

  async function saveMSSB() {
    savingMSSB.value = true;
    try {
      await electricalApi.createMSSB({
        name: mssbForm.value.name,
        description: mssbForm.value.description,
        current_rating: mssbForm.value.current_rating,
        service_type: mssbForm.value.service_type,
        parent_msb: mssbForm.value.parent_msb,
        location: mssbForm.value.location,
      });

      showMSSBDialog.value = false;
      resetMSSBForm();

      $q.notify({
        type: 'positive',
        message: 'MSSB created successfully',
      });

      emit('mssb-created');
      emit('refresh-electrical-hierarchy');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create MSSB: ${error.message}`,
      });
    } finally {
      savingMSSB.value = false;
    }
  }

  // ===================================
  // DDC OPERATIONS
  // ===================================

  function showAddDDCDialog() {
    if (mssbOptions.value.length === 0) {
      $q.notify({
        type: 'warning',
        message: 'Please add at least one MSSB first',
      });
      return;
    }

    resetDDCForm();
    showDDCDialog.value = true;
  }

  function resetDDCForm() {
    ddcForm.value = {
      parent_mssb: null,
      name: '',
      description: '',
      number_of_points: 0,
      controller_type: null,
      location: null,
    };
  }

  async function saveDDC() {
    savingDDC.value = true;
    try {
      await electricalApi.createDDC({
        name: ddcForm.value.name,
        description: ddcForm.value.description,
        number_of_points: ddcForm.value.number_of_points,
        controller_type: ddcForm.value.controller_type,
        parent_mssb: ddcForm.value.parent_mssb,
        location: ddcForm.value.location,
      });

      showDDCDialog.value = false;
      resetDDCForm();

      $q.notify({
        type: 'positive',
        message: 'DDC Panel created successfully',
      });

      emit('ddc-created');
      emit('refresh-electrical-hierarchy');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create DDC Panel: ${error.message}`,
      });
    } finally {
      savingDDC.value = false;
    }
  }

  // ===================================
  // DDC ALLOCATION
  // ===================================

  async function openDdcAllocationDialog(node) {
    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.siteId}/equipment?brick_class=brick:DDC_Panel`);
      if (response.ok) {
        const data = await response.json();
        availableDdcPanels.value = data.data || [];
      }

      ddcAllocationForm.value = {
        equipment_id: node.data.id,
        equipment_name: node.label,
        ddc_panel_id: node.data.controller?.id || null,
        apply_to_devices: true,
      };

      showDdcAllocationDialog.value = true;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load DDC panels',
      });
    }
  }

  async function saveDdcAllocation() {
    if (!ddcAllocationForm.value.ddc_panel_id) {
      $q.notify({
        type: 'warning',
        message: 'Please select a DDC panel',
      });
      return;
    }

    savingDdcAllocation.value = true;
    try {
      const response = await authenticatedFetch(
        `/api/v2/sites/${props.siteId}/equipment/${ddcAllocationForm.value.equipment_id}/allocate-controller`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            controller_id: ddcAllocationForm.value.ddc_panel_id,
            cascade_to_devices: ddcAllocationForm.value.apply_to_devices,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        showDdcAllocationDialog.value = false;

        $q.notify({
          type: 'positive',
          message: 'DDC controller allocated successfully',
          caption: data.data.io_usage
            ? `I/O Usage: ${data.data.io_usage.used}/${data.data.io_usage.total} points`
            : undefined,
        });

        emit('ddc-allocated');
        emit('refresh-electrical-hierarchy');
        emit('refresh-data');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to allocate controller');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to allocate DDC controller',
      });
    } finally {
      savingDdcAllocation.value = false;
    }
  }

  async function deallocateDdcController(node) {
    $q.dialog({
      title: 'Confirm Deallocation',
      message: `Remove DDC controller allocation from ${node.label}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment/${node.data.id}/deallocate-controller`,
          { method: 'DELETE' },
        );

        if (response.ok) {
          $q.notify({
            type: 'positive',
            message: 'DDC controller deallocated successfully',
          });

          emit('ddc-deallocated');
          emit('refresh-electrical-hierarchy');
          emit('refresh-data');
        } else {
          throw new Error('Failed to deallocate controller');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to deallocate DDC controller',
        });
      }
    });
  }

  // ===================================
  // POWER CONNECTION
  // ===================================

  async function openPowerConnectionDialog(node) {
    try {
      const brickClass = node.brick_class || '';
      let validSources = [];

      if (brickClass.includes('DDC_Panel')) {
        const mssbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Mechanical_Services_Switchboard`,
        );
        const dbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Distribution_Board`,
        );

        if (mssbResponse.ok && dbResponse.ok) {
          const mssbData = await mssbResponse.json();
          const dbData = await dbResponse.json();
          validSources = [
            ...(mssbData.data || []).map((eq) => ({ ...eq, source_type: 'MSSB' })),
            ...(dbData.data || []).map((eq) => ({ ...eq, source_type: 'DB' })),
          ];
        }
      } else if (brickClass.includes('Mechanical_Services_Switchboard')) {
        const msbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Main_Switchboard`,
        );
        const mssbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Mechanical_Services_Switchboard`,
        );

        if (msbResponse.ok && mssbResponse.ok) {
          const msbData = await msbResponse.json();
          const mssbData = await mssbResponse.json();
          validSources = [
            ...(msbData.data || []).map((eq) => ({ ...eq, source_type: 'MSB' })),
            ...(mssbData.data || [])
              .filter((eq) => eq.id !== node.data.id)
              .map((eq) => ({ ...eq, source_type: 'MSSB' })),
          ];
        }
      } else {
        const msbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Main_Switchboard`,
        );
        const mssbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Mechanical_Services_Switchboard`,
        );
        const dbResponse = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment?brick_class=brick:Distribution_Board`,
        );

        if (msbResponse.ok && mssbResponse.ok && dbResponse.ok) {
          const msbData = await msbResponse.json();
          const mssbData = await mssbResponse.json();
          const dbData = await dbResponse.json();
          validSources = [
            ...(msbData.data || []).map((eq) => ({ ...eq, source_type: 'MSB' })),
            ...(mssbData.data || []).map((eq) => ({ ...eq, source_type: 'MSSB' })),
            ...(dbData.data || []).map((eq) => ({ ...eq, source_type: 'DB' })),
          ];
        }
      }

      availablePowerSources.value = validSources;

      powerConnectionForm.value = {
        equipment_id: node.data.id,
        equipment_name: node.label,
        power_source_id: node.data.power_source?.id || null,
        power_source_type: '',
      };

      showPowerConnectionDialog.value = true;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load power sources',
      });
    }
  }

  async function savePowerConnection() {
    savingPowerConnection.value = true;
    try {
      await electricalApi.savePowerConnection({
        equipment_id: powerConnectionForm.value.equipment_id,
        power_source_id: powerConnectionForm.value.power_source_id,
      });

      showPowerConnectionDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Power connection saved successfully',
      });

      emit('power-connected');
      emit('refresh-electrical-hierarchy');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to save power connection: ${error.message}`,
      });
    } finally {
      savingPowerConnection.value = false;
    }
  }

  async function disconnectPower(node) {
    $q.dialog({
      title: 'Confirm Disconnection',
      message: `Disconnect power source from ${node.label}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        const response = await authenticatedFetch(
          `/api/v2/sites/${props.siteId}/equipment/${node.data.id}/disconnect-power`,
          { method: 'DELETE' },
        );

        if (response.ok) {
          $q.notify({
            type: 'positive',
            message: 'Power disconnected successfully',
          });

          emit('power-disconnected');
          emit('refresh-electrical-hierarchy');
          emit('refresh-data');
        } else {
          throw new Error('Failed to disconnect power');
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to disconnect power',
        });
      }
    });
  }

  // ===================================
  // SWITCHBOARD CLASSIFICATION
  // ===================================

  function openSwitchboardClassificationDialog(node) {
    switchboardClassificationForm.value = {
      equipment_id: node.data.id,
      classification: node.data.metadata?.power_classification || null,
    };

    showSwitchboardClassificationDialog.value = true;
  }

  async function saveSwitchboardClassification() {
    savingSwitchboardClassification.value = true;
    try {
      await electricalApi.saveSwitchboardClassification(
        switchboardClassificationForm.value.equipment_id,
        { classification: switchboardClassificationForm.value.classification },
      );

      showSwitchboardClassificationDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Switchboard classification updated',
      });

      emit('switchboard-classified');
      emit('refresh-electrical-hierarchy');
      emit('refresh-data');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update classification: ${error.message}`,
      });
    } finally {
      savingSwitchboardClassification.value = false;
    }
  }

  // ===================================
  // SWITCHBOARD MANAGEMENT
  // ===================================

  function openAddSwitchboardDialog(parentNode) {
    if (!parentNode) {
      $q.notify({
        type: 'warning',
        message: 'Please select a parent node in the tree first',
      });
      return;
    }

    let spaceId = null;
    if (parentNode.type === 'space') {
      spaceId = parentNode.data?.id || null;
    } else if (parentNode.type === 'equipment' && parentNode.data?.space_id) {
      spaceId = parentNode.data.space_id;
    }

    parentElectricalNode.value = parentNode;
    selectedExistingSwitchboard.value = null;
    newSwitchboard.value = {
      name: '',
      type: '',
      space_id: spaceId,
      brick_class: '',
      properties: {},
    };
    showAddSwitchboardDialog.value = true;
  }

  function openAddExistingSwitchboardDialog(parentNode = null) {
    parentElectricalNode.value = parentNode;
    selectedExistingSwitchboard.value = null;
    showAddExistingSwitchboardDialog.value = true;
  }

  async function confirmAddSwitchboard() {
    if (!newSwitchboard.value.name || !newSwitchboard.value.type) {
      $q.notify({
        type: 'warning',
        message: 'Please fill in name and type',
      });
      return;
    }

    const typeConfig = switchboardTypes.find((t) => t.value === newSwitchboard.value.type);
    if (!typeConfig) {
      $q.notify({
        type: 'warning',
        message: 'Invalid switchboard type',
      });
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/v2/sites/${props.siteId}/equipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newSwitchboard.value.name,
          brick_class: typeConfig.brick_class,
          space_id: newSwitchboard.value.space_id,
          description: newSwitchboard.value.description || '',
          properties: newSwitchboard.value.properties,
        }),
      });

      if (response.ok) {
        showAddSwitchboardDialog.value = false;

        $q.notify({
          type: 'positive',
          message: `${typeConfig.label} created successfully`,
        });

        emit('switchboard-added');
        emit('refresh-electrical-hierarchy');
        emit('refresh-data');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create switchboard');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to create switchboard',
      });
    }
  }

  async function addExistingSwitchboard() {
    if (!selectedExistingSwitchboard.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a switchboard',
      });
      return;
    }

    if (!parentElectricalNode.value) {
      $q.notify({
        type: 'warning',
        message: 'Please select a parent node first',
      });
      return;
    }

    try {
      const response = await authenticatedFetch('/api/relationships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_id: parentElectricalNode.value.data.id,
          source_type: 'App\\Models\\SiteEquipment',
          target_id: selectedExistingSwitchboard.value.id,
          target_type: 'App\\Models\\SiteEquipment',
          relationship_type: 'feeds',
        }),
      });

      if (response.ok) {
        showAddExistingSwitchboardDialog.value = false;

        $q.notify({
          type: 'positive',
          message: 'Switchboard added to hierarchy',
        });

        emit('switchboard-added');
        emit('refresh-electrical-hierarchy');
      } else {
        throw new Error('Failed to create relationship');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to add switchboard to hierarchy',
      });
    }
  }

  // ===================================
  // UNALLOCATED LIST
  // ===================================

  function showUnallocatedList(type) {
    unallocatedType.value = type;
    unallocatedFilter.value = '';
    showUnallocatedDialog.value = true;
  }

  async function quickAddToTree(switchboard) {
    if (!props.selectedElectricalNode) {
      $q.notify({
        type: 'warning',
        message: 'Please select a parent node in the tree first',
      });
      return;
    }

    try {
      const response = await authenticatedFetch('/api/relationships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_id: props.selectedElectricalNode.data.id,
          source_type: 'App\\Models\\SiteEquipment',
          target_id: switchboard.id,
          target_type: 'App\\Models\\SiteEquipment',
          relationship_type: 'feeds',
        }),
      });

      if (response.ok) {
        showUnallocatedDialog.value = false;

        $q.notify({
          type: 'positive',
          message: `Added ${switchboard.name} to electrical hierarchy`,
        });

        emit('switchboard-added');
        emit('refresh-electrical-hierarchy');
      } else {
        throw new Error('Failed to create relationship');
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to add switchboard to tree',
      });
    }
  }

  // ===================================
  // HELPER FUNCTIONS
  // ===================================

  function getSwitchboardIcon(equipment) {
    const brickClass = equipment.brick_class || '';
    if (brickClass.includes('Main_Switchboard')) {return 'dashboard';}
    if (brickClass.includes('Mechanical_Services_Switchboard')) {return 'settings_input_component';}
    if (brickClass.includes('Distribution_Board')) {return 'view_module';}
    if (brickClass.includes('DDC_Panel')) {return 'developer_board';}
    return 'electrical_services';
  }

  function getSwitchboardColor(equipment) {
    const brickClass = equipment.brick_class || '';
    if (brickClass.includes('Main_Switchboard')) {return 'red';}
    if (brickClass.includes('Mechanical_Services_Switchboard')) {return 'orange';}
    if (brickClass.includes('Distribution_Board')) {return 'blue';}
    if (brickClass.includes('DDC_Panel')) {return 'yellow-8';}
    return 'grey';
  }

  function getEquipmentLocation(equipment) {
    if (!equipment.space) {return 'Unknown location';}
    const parts = [];
    if (equipment.space?.level?.building?.name) {parts.push(equipment.space.level.building.name);}
    if (equipment.space?.level?.name) {parts.push(equipment.space.level.name);}
    if (equipment.space?.name) {parts.push(equipment.space.name);}
    return parts.join(' > ') || 'Unknown location';
  }

  function canQuickAdd(switchboard) {
    if (!props.selectedElectricalNode) {return false;}

    const parentClass = props.selectedElectricalNode.brick_class || '';
    const switchboardClass = switchboard.brick_class || '';

    if (parentClass.includes('Transformer')) {
      return switchboardClass === 'brick:Main_Switchboard';
    } else if (parentClass.includes('Main_Switchboard')) {
      return switchboardClass === 'brick:Mechanical_Services_Switchboard' ||
        switchboardClass === 'brick:Distribution_Board';
    } else if (parentClass.includes('Mechanical_Services_Switchboard')) {
      return switchboardClass === 'brick:DDC_Panel' ||
        switchboardClass === 'brick:Distribution_Board';
    }

    return false;
  }

  function getUnallocatedBadgeColor() {
    switch (unallocatedType.value) {
      case 'MSB': return 'red';
      case 'MSSB': return 'orange';
      case 'DB': return 'blue';
      case 'DDC': return 'yellow-8';
      default: return 'grey';
    }
  }

  function getUnallocatedCount() {
    switch (unallocatedType.value) {
      case 'MSB': return unallocatedSwitchboards.value.msb.length;
      case 'MSSB': return unallocatedSwitchboards.value.mssb.length;
      case 'DB': return unallocatedSwitchboards.value.db.length;
      case 'DDC': return unallocatedSwitchboards.value.ddc.length;
      default: return 0;
    }
  }

  // ===================================
  // EXPOSE PUBLIC METHODS
  // ===================================
  defineExpose({
    // MSB operations
    showAddMSBDialog,

    // MSSB operations
    showAddMSSBDialog,

    // DDC operations
    showAddDDCDialog,

    // Allocation operations
    openDdcAllocationDialog,
    deallocateDdcController,

    // Power operations
    openPowerConnectionDialog,
    disconnectPower,

    // Classification
    openSwitchboardClassificationDialog,

    // Switchboard management
    openAddSwitchboardDialog,
    openAddExistingSwitchboardDialog,
    showUnallocatedList,
  });
</script>

<style scoped>
.electrical-manager {
  /* Container styles - invisible wrapper */
}
</style>
